"use client";

import { useTranslations } from "next-intl";
import React, { startTransition, useState } from "react";
import { cn } from "../../helpers/classNames";
import { formatLiterPerSquareMeter } from "../../helpers/formatters/literPerSquareMeter";
import { formatPercentage } from "../../helpers/formatters/percentage";
import { formatTemperature } from "../../helpers/formatters/temperature";
import type { TranslationKey } from "../../i18n/types";
import { getGusts } from "../../network/matlocq/helpers/forecast/getGusts";
import type { ForecastSpace } from "../../network/matlocq/types/forecastSpace";
import { Button } from "../Button";
import { useDayjs } from "../Dayjs/hooks";
import { WeatherIcon } from "../WeatherIcon";
import { WindDirectionIcon } from "../WindDirectionIcon";
import styles from "./styles.module.scss";

type Props = {
    forecastSpaces: Array<ForecastSpace>;
    timezone: string;
};

export const Tables: React.FC<Props> = ({ forecastSpaces, timezone }) => {
    const t = useTranslations("component.forecastSpacesTable");
    const [showMore, setShowMore] = useState(false);
    const dayjs = useDayjs();

    // if index=0 set to -1 because we don't have a day switch, because all 4 spaces are from today
    const indexDayChange =
        forecastSpaces.findIndex((space) => space.type === "morning") || -1;

    return (
        <>
            <table className={styles.table}>
                <colgroup span={indexDayChange === -1 ? 4 : indexDayChange} />
                {indexDayChange !== -1 && (
                    <colgroup span={4 - indexDayChange} />
                )}
                <thead>
                    <tr>
                        {forecastSpaces.map((space, index) =>
                            index === 0 || indexDayChange === index ? (
                                <th
                                    key={space.from}
                                    className={cn(
                                        styles.th,
                                        styles.fontBlue,
                                        styles.fontLeft,
                                        indexDayChange === index &&
                                            styles.tdDayChange
                                    )}
                                    colSpan={
                                        index === 0
                                            ? indexDayChange === -1
                                                ? 4
                                                : indexDayChange
                                            : forecastSpaces.length -
                                              indexDayChange
                                    }
                                    scope="colspan"
                                >
                                    {dayjs(space.from)
                                        .tz(timezone)
                                        .format("dd DD.MM.")}
                                </th>
                            ) : null
                        )}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <th
                                key={space.type}
                                className={cn(
                                    styles.th,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                                scope="col"
                            >
                                <div className={styles.fontBold}>
                                    {space.type
                                        ? t(
                                              `spaces.${space.type}` as TranslationKey<
                                                  typeof t
                                              >
                                          )
                                        : null}
                                </div>
                                <div>
                                    {t("labels.spaceDuration", {
                                        from: dayjs(space.from)
                                            .tz(timezone)
                                            .format("HH"),
                                        to: dayjs(space.to)
                                            .tz(timezone)
                                            .format("HH"),
                                    })}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                <WeatherIcon
                                    imgTagSize={{ width: 54, height: 38 }}
                                    url={
                                        space.weatherAggregated.iconUrl ?? null
                                    }
                                    alt={space.weatherAggregated.text ?? ""}
                                    title={space.weatherAggregated.text ?? null}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                <div className={styles.temperature}>
                                    <span>
                                        {formatTemperature(
                                            space.temperature.max
                                        )}
                                    </span>
                                    <span>
                                        {` / ${formatTemperature(
                                            space.temperature.min
                                        )}`}
                                    </span>
                                </div>
                            </td>
                        ))}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                {space.weatherAggregated.text ?? ""}
                            </td>
                        ))}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                <div className={styles.detailContainer}>
                                    <div
                                        className={cn(
                                            styles.detailContainerIcon,
                                            "icon-umbrella-filled"
                                        )}
                                        title={t("description.precipitation")}
                                    />
                                    <div className={styles.detailContainerText}>
                                        <div>
                                            {formatPercentage(
                                                space.prec.probability
                                            )}
                                        </div>
                                        <div>
                                            {space.prec.sum !== null &&
                                            space.prec.sum > 0
                                                ? formatLiterPerSquareMeter(
                                                      space.prec.sum
                                                  )
                                                : null}
                                        </div>
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                </tbody>
                <tbody
                    aria-labelledby="moreWeatherInfos"
                    className={cn(showMore ? styles.shown : styles.hidden)}
                >
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => {
                            const gusts = getGusts(
                                space.wind.avg,
                                space.wind.gusts.value
                            );
                            return (
                                <td
                                    key={space.type}
                                    className={cn(
                                        styles.td,
                                        indexDayChange === index &&
                                            styles.tdDayChange
                                    )}
                                >
                                    <div className={styles.detailContainer}>
                                        <div
                                            className={
                                                styles.detailContainerIcon
                                            }
                                        >
                                            <div className={styles.colorBlue}>
                                                <WindDirectionIcon
                                                    degree={space.wind.degree}
                                                    title={t(
                                                        "description.wind",
                                                        {
                                                            direction:
                                                                space.wind
                                                                    .direction ??
                                                                "",
                                                        }
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                styles.detailContainerText
                                            }
                                        >
                                            <div>
                                                {space.wind.text?.toUpperCase() ??
                                                    ""}
                                            </div>
                                            <div>
                                                {space.wind.avg
                                                    ? `${space.wind.avg.toString()} ${
                                                          space.wind.unit?.toString() ??
                                                          ""
                                                      }`
                                                    : null}
                                            </div>
                                            {gusts !== null ? (
                                                <>
                                                    <div>
                                                        {t("labels.gusts")}
                                                    </div>
                                                    <div>
                                                        {`${gusts.toString()} ${
                                                            space.wind.unit?.toString() ??
                                                            ""
                                                        }`}
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                <div className={styles.detailContainer}>
                                    <div className={styles.detailContainerIcon}>
                                        <div
                                            className={cn(
                                                styles.colorRed,
                                                "icon-heart"
                                            )}
                                        />
                                    </div>
                                    <div className={styles.detailContainerText}>
                                        {t("labels.windchill", {
                                            max: formatTemperature(
                                                space.windchill.max
                                            ),
                                            min: formatTemperature(
                                                space.windchill.min
                                            ),
                                        })}
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                <div className={styles.detailContainer}>
                                    <div className={styles.detailContainerIcon}>
                                        <div
                                            className={cn(
                                                styles.colorBlue,
                                                "icon-relative-humidity"
                                            )}
                                        />
                                    </div>
                                    <div className={styles.detailContainerText}>
                                        {space.relativeHumidity.avg
                                            ? `${space.relativeHumidity.avg.toString()} %`
                                            : null}
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                    <tr className={styles.textCenter}>
                        {forecastSpaces.map((space, index) => (
                            <td
                                key={space.type}
                                className={cn(
                                    styles.td,
                                    indexDayChange === index &&
                                        styles.tdDayChange
                                )}
                            >
                                <div className={styles.detailContainer}>
                                    <div className={styles.detailContainerIcon}>
                                        <div
                                            className={cn(
                                                styles.colorBlue,
                                                "icon-barometric-pressure"
                                            )}
                                        />
                                    </div>
                                    <div className={styles.detailContainerText}>
                                        {space.pressure.avg
                                            ? `${space.pressure.avg.toString()} hPa`
                                            : null}
                                    </div>
                                </div>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
            <Button
                className={styles.styledButton}
                variant="secondary"
                fullWidth
                dataLabel="VHS_mehr_Wetterinfos_heute"
                onClick={() => startTransition(() => setShowMore(!showMore))}
                id="moreWeatherInfos"
            >
                {showMore ? t("cta.less") : t("cta.more")}
                <span
                    className={cn(
                        "icon-dropdown",
                        styles.arrowIcon,
                        showMore && styles.arrowIconOpen
                    )}
                />
            </Button>
        </>
    );
};
